import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
  
  ignoreSwitch: {
		marginTop: theme.spacing(2),
		display: "flex",
		alignItems: "center",
	},
}));

const ContactSchema = ({ canViewNumber }) => 
  Yup.object().shape({
    name: Yup.string()
      .min(2, "¡Demasiado corto!")
      .max(50, "¡Demasiado largo!")
      .required("Este campo es obligatorio")
      .test("log-name", function(value) {
        return true;
      }),

    number: Yup.string().when("isGroup", {
      is: false,
      then: (schema) => {
        if (canViewNumber) {
          return schema
            .min(8, "¡Demasiado corto!")
            .max(50, "¡Demasiado largo!")
            .matches(/^\+?[\d\s()-]+$/, {
              message:
                "Ingresá un número válido; puede incluir +, espacios, guiones y paréntesis.",
              excludeEmptyString: true
            })
            .required("Este campo es obligatorio");
        }
        return schema.notRequired(); 
      },
      otherwise: (schema) =>
        schema
          .min(10, "¡ID de grupo demasiado corto!")
          .required("El ID del grupo es obligatorio"),
    }),

    email: Yup.string().email("Email inválido")
      .test("log-email", function(value) {
        return true;
      }),

    link: Yup.string().url("Debe ser una URL válida (ej: https://...)").nullable()
      .test("log-link", function(value) {
        return true;
      }),

    extraInfo: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .required("El nombre es obligatorio")
          .test(
            "is-unique",
            "Este nombre de campo ya existe",
            function (value) {
              if (!value) {
                return true;
              }

              const fullFormValues = this.from[0].value;

              const extraInfoArray = fullFormValues.extraInfo || [];

              const currentItem = this.parent;

              const count = extraInfoArray.filter(
                (item) =>
                  item !== currentItem && 
                  item.name && 
                  item.name.toLowerCase() === value.toLowerCase()
              ).length;

              return count === 0;
            }
          ),
        value: Yup.string(),
      })
    ),
  });

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

  const [formInitialValues, setFormInitialValues] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (!contactId && !initialValues) {
				setFormInitialValues({
					name: "", number: "", email: "", isIgnored: false,
					isGroup: false, extraInfo: [], link: "",
				});
				return;
			}
			
			setLoading(true);
			try {
				let dataToProcess = null;
				if (initialValues) {
					dataToProcess = { ...initialValues };
				} else if (contactId) {
					const { data } = await api.get(`/contacts/${contactId}`);
					dataToProcess = data;
				}

				if (dataToProcess) {
					const linkInfo = dataToProcess.extraInfo?.find(info => info.name === 'Link');
					const otherInfo = dataToProcess.extraInfo?.filter(info => info.name !== 'Link') || [];
					
					const finalValues = {
						name: dataToProcess.name || "",
						number: dataToProcess.number || "",
						email: dataToProcess.email || "",
						isIgnored: dataToProcess.isIgnored || false,
						isGroup: dataToProcess.isGroup || false,
						link: linkInfo ? linkInfo.value : "",
						extraInfo: otherInfo,
					};
					setFormInitialValues(finalValues);
				}
			} catch (err) {
				toastError(err);
			}
			setLoading(false);
		};

		if (open) {
			fetchContact();
		}

	}, [contactId, open, initialValues]);

	const handleClose = () => {
		setFormInitialValues(null); 
		onClose();
	};

	const { user } = useContext(AuthContext);
  const canViewNumber = user.profile === "admin" || user.profile === "superadmin" || user.canViewNumbers;
      
	const handleSaveContact = async values => {
    const dataToSend = JSON.parse(JSON.stringify(values));
		const linkValue = dataToSend.link?.trim();
		
    if (!canViewNumber && contactId) {
        delete dataToSend.number;
    }
    
		delete dataToSend.link;

		const otherInfo = dataToSend.extraInfo.filter(info => info.name !== 'Link');
		dataToSend.extraInfo = otherInfo;

		if (linkValue) {
			dataToSend.extraInfo.push({ name: 'Link', value: linkValue });
		}

		try {
			const valuesWithCompany = {
				...dataToSend,
				companyId: user?.companyId
			};
			if (contactId) {
				await api.put(`/contacts/${contactId}`, valuesWithCompany);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", valuesWithCompany);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				
				{loading || !formInitialValues ? (
					<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
						<CircularProgress />
					</div>
				) : (
					<Formik
            initialValues={formInitialValues}
            enableReinitialize
            validationSchema={ContactSchema({ 
              canViewNumber, 
            })}
            context={{ canViewNumber: canViewNumber }} 
            onSubmit={async (values, actions) => {
              try {
                await handleSaveContact(values);
              } catch (err) {
                console.error("💥 Error en handleSaveContact:", err);
              } finally {
                actions.setSubmitting(false);
              }
            }}
          >
						{({ values, errors, touched, isSubmitting, setFieldValue }) => {
              return (
                <Form>
                  <DialogContent dividers>
                    <Typography variant="subtitle1" gutterBottom>
                      {i18n.t("contactModal.form.mainInfo")}
                    </Typography>
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.name")}
                      name="name"
                      autoFocus
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.number")}
                      name="number"
                      placeholder="Ej: 5513912344321"
                      variant="outlined"
                      margin="dense"
                      disabled={!canViewNumber && contactId}
                      value={canViewNumber ? values.number : "Número Protegido"}
                      InputLabelProps={{ shrink: true }}
                    />
                    <div>
                      <Field
                        as={TextField}
                        label={i18n.t("contactModal.form.email")}
                        name="email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        placeholder="Correo electrónico"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                      />
                    </div>
                    <div>
                      <Field
                        as={TextField}
                        label="Link"
                        name="link"
                        error={touched.link && Boolean(errors.link)}
                        helperText={touched.link && errors.link}
                        placeholder="https://ejemplo.com/perfil"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                      />
                    </div>
                    <div className={classes.ignoreSwitch}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.isIgnored || false}
                            onChange={(e) => {
                              setFieldValue("isIgnored", e.target.checked);
                            }}
                            name="isIgnored"
                            color="primary"
                          />
                        }
                        label="Ignorar Contacto"
                      />
                      <Tooltip
                        title="Si está activado, no se recibirán nuevos mensajes ni se crearán tickets para este contacto."
                        arrow
                      >
                        <InfoOutlinedIcon color="action" style={{ marginLeft: 4, fontSize: 18 }} />
                      </Tooltip>
                    </div>
                    <Typography
                      style={{ marginBottom: 8, marginTop: 12 }}
                      variant="subtitle1"
                    >
                      {i18n.t("contactModal.form.extraInfo")}
                    </Typography>
                    <FieldArray name="extraInfo">
                      {({ push, remove }) => (
                        <>
                          {values.extraInfo &&
                            values.extraInfo.length > 0 &&
                            values.extraInfo.map((info, index) => (
                              <div
                                className={classes.extraAttr}
                                key={`${index}-info`}
                              >
                                <Field
                                  as={TextField}
                                  label={i18n.t("contactModal.form.extraName")}
                                  name={`extraInfo[${index}].name`}
                                  variant="outlined"
                                  margin="dense"
                                  className={classes.textField}
                                  error={
                                    touched.extraInfo?.[index]?.name &&
                                    Boolean(errors.extraInfo?.[index]?.name)
                                  }
                                  helperText={
                                    touched.extraInfo?.[index]?.name &&
                                    errors.extraInfo?.[index]?.name
                                  }
                                />
                                <Field
                                  as={TextField}
                                  label={i18n.t("contactModal.form.extraValue")}
                                  name={`extraInfo[${index}].value`}
                                  variant="outlined"
                                  margin="dense"
                                  className={classes.textField}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </div>
                            ))}
                          <div className={classes.extraAttr}>
                            <Button
                              style={{ flex: 1, marginTop: 8 }}
                              variant="outlined"
                              color="primary"
                              onClick={() => push({ name: "", value: "" })}
                            >
                              {`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
                            </Button>
                          </div>
                        </>
                      )}
                    </FieldArray>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      disabled={isSubmitting}
                      variant="outlined"
                    >
                      {i18n.t("contactModal.buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                      variant="contained"
                      className={classes.btnWrapper}
                    >
                      {contactId
                        ? `${i18n.t("contactModal.buttons.okEdit")}`
                        : `${i18n.t("contactModal.buttons.okAdd")}`}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  </DialogActions>
                </Form>
              );
            }}
					</Formik>
				)}
			</Dialog>
		</div>
	);
};

export default ContactModal;
